import React, { useState } from "react";
import { Form, Input, DatePicker, Radio, Button, Steps, InputNumber, Drawer, Grid } from "antd";
import TextArea from "antd/es/input/TextArea";
import { DataTable, PageCard, PageShell } from "../../components/ui";
import { useTranslation } from "react-i18next";

const { Step } = Steps;

const Form43 = () => {
    const { t } = useTranslation();
    const [current, setCurrent] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [visible, setVisible] = useState(false);
    const screens = Grid.useBreakpoint();

    const next = () => {
        setCurrent(current + 1);
    };

    const prev = () => {
        setCurrent(current - 1);
    };

    const onChange = (value) => {
        setCurrent(value);
    };

    const closeDrawer = () => {
        setVisible(false);
    };

    const teeth = [
        { key: "8", label: "8" },
        { key: "7", label: "7" },
        { key: "6", label: "6" },
        { key: "5", label: "5" },
        { key: "4", label: "4" },
        { key: "3", label: "3" },
        { key: "2", label: "2" },
        { key: "1", label: "1" },
        { key: "1_2", label: "1" },
        { key: "2_2", label: "2" },
        { key: "3_2", label: "3" },
        { key: "4_2", label: "4" },
        { key: "5_2", label: "5" },
        { key: "6_2", label: "6" },
        { key: "7_2", label: "7" },
        { key: "8_2", label: "8" },
    ];

    const columns = [
        {
            fixed: 'left',
            title: t('form43.teethCondition'),
            dataIndex: "condition",
            key: "condition",
            width: 220,
            render: (text) => <span className="text-sm font-medium text-[#1D2C7B]">{text}</span>,
        },
        ...teeth.map(({ key, label }) => ({
            title: label,
            dataIndex: key,
            key,
            width: 72,
            render: () => <InputNumber size="small" className="!w-full" />,
        })),
    ];

    const data = [
        { key: "1", condition: t('form43.condExtracted') },
        { key: "2", condition: t('form43.condRoot') },
        { key: "3", condition: t('form43.condCaries') },
        { key: "4", condition: t('form43.condPulpitis') },
        { key: "5", condition: t('form43.condPeriodontitis') },
        { key: "6", condition: t('form43.condMobility') },
        { key: "7", condition: t('form43.condFilling') },
        { key: "8", condition: t('form43.condArtificial') },
    ];

    const renderToothMobileCard = (record) => (
        <div>
            <p className="m-0 text-sm font-semibold text-[#1D2C7B]">{record.condition}</p>
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {teeth.map(({ key, label }) => (
                    <label key={key} className="flex shrink-0 flex-col items-center gap-1">
                        <span className="text-xs text-slate-500 tabular-nums">{label}</span>
                        <InputNumber size="small" className="!w-14" />
                    </label>
                ))}
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard className="p-3 sm:p-5 md:p-8">
                <div className="flex flex-col items-center justify-between gap-3 md:flex-row md:gap-6">
                    <h1 className="m-0 text-center text-xs font-medium uppercase font-roman text-slate-800 sm:text-sm md:text-left md:text-base">
                        {t('form43.headerLeft')}
                    </h1>
                    <img src="/uzb.jpg" alt="" className="h-14 shrink-0 md:h-20" />
                    <h1 className="m-0 text-center text-xs font-medium uppercase font-roman text-slate-800 sm:text-sm md:text-right md:text-base">
                        {t('form43.headerRight')}
                    </h1>
                </div>

                <h1 className="mt-4 mb-6 text-center text-lg font-bold font-roman text-slate-900 sm:text-xl md:text-2xl">
                    {t('form43.title')}
                </h1>

                {/* Steps component */}
                <div className="flex flex-col gap-5 lg:flex-row lg:gap-8">
                    <div className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[25%] lg:self-start lg:border-r lg:border-slate-100 lg:pr-4">
                        <Steps
                            onChange={onChange}
                            direction={screens.lg ? "vertical" : "horizontal"}
                            current={current}
                            size="small"
                        >
                            <Step title={t('form43.part1')} />
                            <Step title={t('form43.part2')} />
                            <Step title={t('form43.part3')} />
                            <Step title={t('form43.part4')} />
                        </Steps>
                    </div>

                    <div className="w-full min-w-0">
                        {current === 0 && (
                            <Form className="w-full" layout="vertical">
                                <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                                    <div>
                                        <Form.Item label={t('form43.fullName')} name="name">
                                            <Input placeholder={t('form43.fullName')}/>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <Form.Item label={t('form43.birthYear')} name="birthDate">
                                            <DatePicker placeholder={t('form43.birthYear')} className="w-full"/>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <Form.Item label={t('patientCard.gender')} name="gender">
                                            <Radio.Group>
                                                <Radio value="male">{t('patientCard.male')}</Radio>
                                                <Radio value="female">{t('patientCard.female')}</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <Form.Item label={t('form43.address')} name="address">
                                            <Input placeholder={t('form43.address')}/>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <Form.Item label={t('form43.job')} name="job">
                                            <Input placeholder={t('form43.job')}/>
                                        </Form.Item>
                                    </div>
                                </div>
                                <Form.Item label={t('form43.diagnosis')} name="diagnosis">
                                    <TextArea rows={2} placeholder={t('form43.diagnosis')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.complaints')} name="complaints">
                                    <TextArea rows={2} placeholder={t('form43.complaints')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.childhoodHistory')} name="history">
                                    <TextArea rows={4} placeholder={t('form43.childhoodHistoryPh')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.currentHistory')} name="currentHistory">
                                    <TextArea rows={4} placeholder={t('form43.currentHistoryPh')}/>
                                </Form.Item>
                            </Form>
                        )}

                        {current === 1 && (
                            <Form className="w-full" layout="vertical">
                                <Form.Item label={t('form43.epidemQ1')}
                                           name="infectionContact">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ1')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.epidemQ2')}
                                           name="travelHistory">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ2Ph')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.epidemQ3')}
                                           name="infectiousDiseases">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ3')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.epidemQ4')} name="hospitalization">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ4')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.epidemQ5')} name="parenteralTreatment">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ5')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.epidemQ6')} name="householdServices">
                                    <TextArea rows={4} placeholder={t('form43.epidemQ6')}/>
                                </Form.Item>
                                <Form.Item label={t('form43.allergy')}
                                           name="allergyHistory">
                                    <TextArea rows={2} placeholder={t('form43.allergyPh')}/>
                                </Form.Item>
                                <Form.Item
                                    label={t('form43.clinicalDiagnosis')}
                                    name="diagnosis">
                                    <TextArea rows={2} placeholder={t('form43.diagnosis')}/>
                                </Form.Item>
                            </Form>
                        )}

                        {current === 2 && (
                            <Form className="w-full" layout="vertical">
                                <h3 className="mb-3 text-base font-semibold text-slate-800 sm:text-lg">{t('form43.appearance')}</h3>
                                <DataTable
                                    columns={columns}
                                    dataSource={data}
                                    bordered
                                    pagination={false}
                                    rowKey="key"
                                    scrollX="max-content"
                                    mobileCard={renderToothMobileCard}
                                />
                            </Form>
                        )}

                        {current === 3 && (
                            <Form className="w-full" layout="vertical">
                                <Form.Item label={t('form43.treatment')} name="treatmentPlan">
                                    <TextArea rows={10} placeholder={t('form43.treatment')}/>
                                </Form.Item>
                            </Form>
                        )}

                    </div>
                </div>

                <Form className="w-full" layout="vertical">
                    {/* Navigation buttons */}
                    <div className="mt-6 flex items-center justify-between gap-3">
                        <Button onClick={prev} disabled={current === 0}>
                            {t('common.back')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={next}
                            disabled={current === 3}
                            style={current === 3 ? undefined : { backgroundColor: '#1D2C7B', borderColor: '#1D2C7B' }}
                        >
                            {t('common.next')}
                        </Button>
                    </div>
                </Form>
            </PageCard>
        </PageShell>
    );
};

export default Form43;
